// import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, ReplaySubject } from 'rxjs'
import { distinctUntilChanged, filter, map, mergeMap, switchMap, take, tap, withLatestFrom } from 'rxjs/operators'
import { Entity, EntityData, Field } from '../../../modules/dto'
import { isEqual } from 'lodash'
import { StoredEntityService } from './stored-entity.service'
import { AdditionalQualifier } from '../../dto/models/table-state.model'

const TUPLE_POS_ENTITY = 0
const TUPLE_POS_ROW_NUMBER = 1
const ENTITY_KEY_FIELDS = 'fields'
const ENTITY_FIELD_KEY_LAST_UPDATED = '_lastUpdate_'
const ENTITY_FIELD_KEY_ID = 'id'

/** Keeps track of a set of entity (e.g. for a page of a dynamic-table) from the store for a single component (and its children).
 * For this to work the service has to be provided to the consuming components directly, not over modules or 'root'.
 * Like:
 *    @Component({
 *      providers: [StoredEntityService]
 *    })
 *
 * Usage:
 * - subscribe to entities$-observable.
 * - add new versions of entity-sets with:
 *      initOrUpdateWithEntities(entities: Entity[]) or
 *      initOrUpdateWithEntityData(entityData: EntityData)
 *
 *    these entities will be fed to the store and will then be send to the entities$-observable
 *    as will all other updates regarding these entities from other sources.
 */
// @Injectable({
//   providedIn: 'any'
// })
export class StoredEntitiesWrapper {
  /** currently selected entities */
  entities$: Observable<Entity[]>
  // we should split entity-data and entities for good.
  // entities2$: Observable<Entity>[];
  entities2$: Observable<[Entity, number]>
  entities3$: Observable<[Entity, number]>

  singleRowUpdate$: Observable<[Entity, number]>
  pageUpdate$: Observable<Entity[]>

  private _$entityData: ReplaySubject<EntityData> = new ReplaySubject<EntityData>(1)
  /** without actual entities. just the other data */
  entityData$: Observable<EntityData> = this._$entityData.asObservable()

  /** stores the entity-type */
  private _$type: ReplaySubject<string> = new ReplaySubject<string>(1)

  /** manages the entity-type (ensures distinct values) */
  private _type$: Observable<string> = this._$type.asObservable().pipe(distinctUntilChanged())

  /** stores ids of current entities */
  private _$ids: ReplaySubject<number[]> = new ReplaySubject<number[]>(1)

  /** manages ids of current entities (ensures distinct values*/
  private _ids$: Observable<number[]> = this._$ids.asObservable().pipe(
    // isEqual checks identity and content
    distinctUntilChanged((a, b) => isEqual(a, b))
  )

  private _$lastEmittedEntities: BehaviorSubject<[Entity, number][]> = new BehaviorSubject<[Entity, number][]>(null)

  constructor(
    // private entityFacade: EntityFacadeService,
    private beAndStoreEntityService: StoredEntityService
  ) {
    console.log('initializing stored-entities-service.')
    // initialize entities-observable from type and selected ids
    this.entities$ = combineLatest([this._type$, this._ids$]).pipe(
      // check that all values are initialized
      filter(([type, ids]) => !!type && !!ids),
      tap(([type, ids]) => console.log('type, ids: ', type, ids)),
      // get actual entity-objects
      switchMap(([type, ids]) => this.beAndStoreEntityService.getEntities$(type, ids)),
      // tap(entities => console.log('current entities pre distinct: ', entities)),
      // distinct should be done deeper.
      distinctUntilChanged((a, b) => isEqual(a, b)),
      tap((entities) => console.log('current entities: ', entities))
    )

    this.entities3$ = combineLatest([this._type$, this._ids$]).pipe(
      // check that all values are initialized
      filter(([type, ids]) => !!type && !!ids),
      // tap(([type, ids]) => console.log('entities3 - type, ids: ', type, ids)),
      // get entities from type and ids
      switchMap(([type, ids]) => this.beAndStoreEntityService.getEntities$(type, ids)),
      // map entities to entity and row-number
      map((entities) => entities.map((e, i) => [e, i] as [Entity, number])),
      // get previous state of entity and row-number data
      withLatestFrom(this._$lastEmittedEntities),
      map(([entities, lastEntities]) => {
        // console.log('comparing old and new values', entities, lastEntities);
        // catch initial undefined state
        if (!entities) {
          return entities
        }
        // filter out unchanged entities
        const updatedEntities = entities?.filter((e) => {
          // if new row is added, don't filter it out
          const lastEntitiesContainsRowId = lastEntities?.some(
            (le) => e[TUPLE_POS_ROW_NUMBER] === le[TUPLE_POS_ROW_NUMBER]
          )
          if (!lastEntitiesContainsRowId) {
            return true
          }
          // compare _lastUpdate_ field of current and previous version of entity for current row
          const currentVersion = e[TUPLE_POS_ENTITY][ENTITY_KEY_FIELDS]
          const prevVersion = lastEntities[e[TUPLE_POS_ROW_NUMBER]][TUPLE_POS_ENTITY][ENTITY_KEY_FIELDS]
          const lastUpdatedValueCurrentVersion = currentVersion[ENTITY_FIELD_KEY_LAST_UPDATED]
          const lastUpdatedValuePreviousVersion = prevVersion[ENTITY_FIELD_KEY_LAST_UPDATED]

          return lastUpdatedValueCurrentVersion !== lastUpdatedValuePreviousVersion
        })
        // adding 'null' updates for removed rows (e.g. for the last page that has only part of the page-size)
        // can't be in here if only row updates are sent
        updatedEntities.concat(
          lastEntities
            ?.filter((le) => !updatedEntities.some((ue) => ue[TUPLE_POS_ROW_NUMBER] === le[TUPLE_POS_ROW_NUMBER]))
            .map((deletedEntries) => [null, deletedEntries[TUPLE_POS_ROW_NUMBER]] as [Entity, number]) || []
        )

        // update previous state
        this._$lastEmittedEntities.next(entities)
        // return entities with new state
        return updatedEntities
      }),
      // this converts the observable of an array with entity-position pairs into a observable of singly firing entity-pos pairs.
      // Observable<[Entity, number][]> -> Observable<[Entity, number]>
      // (resulting observables fires n times, where n is the length of the input array)
      mergeMap((entitiesWithPos) => entitiesWithPos)
    )

    const withLastEmittedValues$ = combineLatest([this._type$, this._ids$]).pipe(
      // check that all values are initialized
      filter(([type, ids]) => !!type && !!ids),
      // tap(([type, ids]) => console.log('entities3 - type, ids: ', type, ids)),
      // get entities from type and ids
      switchMap(([type, ids]) => this.beAndStoreEntityService.getEntities$(type, ids)),
      // map entities to entity and row-number
      map((entities) => entities.map((e, i) => [e, i] as [Entity, number])),
      // get previous state of entity and row-number data
      withLatestFrom(this._$lastEmittedEntities)
    )

    this.singleRowUpdate$ = withLastEmittedValues$.pipe(
      filter(([entities, lastEntities]) => {
        // what happens with empty values? (initial values, ...)
        // removing events that should be a page reload
        // ... different page length
        if (entities.length !== lastEntities.length) {
          // this._$lastEmittedEntities.next(entities);
          return false
        }
        return (
          entities.filter((e) =>
            lastEntities.some(
              (le) =>
                le[TUPLE_POS_ROW_NUMBER] === e[TUPLE_POS_ROW_NUMBER] &&
                !this.idAndLastUpdateEqual(e[TUPLE_POS_ENTITY], le[TUPLE_POS_ENTITY])
            )
          ).length === 1
        )
      }),
      map(([entities, lastEntities]) => {
        // console.log('comparing old and new values', entities, lastEntities);
        // catch initial undefined state
        if (!entities) {
          return entities
        }
        // filter out unchanged entities
        const updatedEntities = entities?.filter((e) => {
          // if new row is added, don't filter it out
          const lastEntitiesContainsRowId = lastEntities?.some(
            (le) => e[TUPLE_POS_ROW_NUMBER] === le[TUPLE_POS_ROW_NUMBER]
          )
          if (!lastEntitiesContainsRowId) {
            return true
          }
          // compare _lastUpdate_ field of current and previous version of entity for current row
          const currentVersion = e[TUPLE_POS_ENTITY][ENTITY_KEY_FIELDS]
          const prevVersion = lastEntities[e[TUPLE_POS_ROW_NUMBER]][TUPLE_POS_ENTITY][ENTITY_KEY_FIELDS]
          const lastUpdatedValueCurrentVersion = currentVersion[ENTITY_FIELD_KEY_LAST_UPDATED]
          const lastUpdatedValuePreviousVersion = prevVersion[ENTITY_FIELD_KEY_LAST_UPDATED]

          return lastUpdatedValueCurrentVersion !== lastUpdatedValuePreviousVersion
        })
        // adding 'null' updates for removed rows (e.g. for the last page that has only part of the page-size)
        // can't be in here if only row updates are sent
        updatedEntities.concat(
          lastEntities
            ?.filter((le) => !updatedEntities.some((ue) => ue[TUPLE_POS_ROW_NUMBER] === le[TUPLE_POS_ROW_NUMBER]))
            .map((deletedEntries) => [null, deletedEntries[TUPLE_POS_ROW_NUMBER]] as [Entity, number]) || []
        )

        // update previous state
        this._$lastEmittedEntities.next(entities)
        // return entities with new state
        return updatedEntities
      }),
      // this converts the observable of an array with entity-position pairs into a observable of singly firing entity-pos pairs.
      // Observable<[Entity, number][]> -> Observable<[Entity, number]>
      // (resulting observables fires n times, where n is the length of the input array)
      mergeMap((entitiesWithPos) => entitiesWithPos)
    )

    this.pageUpdate$ = withLastEmittedValues$.pipe(
      filter(([entities, lastEntities]) => {
        // what happens with empty values? (initial values, ...)
        // removing events that should be a page reload
        // ... different page length
        if (entities.length !== lastEntities.length) {
          // this._$lastEmittedEntities.next(entities);
          return true
        }
        return (
          entities.filter((e) =>
            lastEntities.some(
              (le) =>
                le[TUPLE_POS_ROW_NUMBER] === e[TUPLE_POS_ROW_NUMBER] &&
                !this.idAndLastUpdateEqual(e[TUPLE_POS_ENTITY], le[TUPLE_POS_ENTITY])
            )
          ).length > 1
        )
      }),
      map(([entities, lastEntities]) => entities),
      tap((entities) => this._$lastEmittedEntities.next(entities)),
      map((entities) => entities.map((e) => e[TUPLE_POS_ENTITY]))
    )
  }

  filter$(
    fields: Field[],
    type: string,
    page: number,
    pageSize: number,
    mainId: number,
    qualifier: string,
    sorting: string,
    additionalQualifier?: AdditionalQualifier
  ): Observable<EntityData> {
    this._$type.next(type)
    return this.beAndStoreEntityService
      .filter$(fields, type, page, pageSize, mainId, qualifier, sorting, additionalQualifier)
      .pipe(
        tap((entityData) => {
          if (!entityData.data) {
            console.log('can not init ids')
            return
          }
          const ids = entityData.data?.map((e) => e['id'])
          console.log('new ids: ', ids)
          this._$ids.next(ids)
        }),
        // tap(entityData => this._$entityData.next({...entityData, data: []} as EntityData))
        tap((entityData) => this._$entityData.next(entityData))
      )
  }

  filter(
    fields: Field[],
    type: string,
    page: number,
    pageSize: number,
    mainId: number,
    qualifier: string,
    sorting: string,
    additionalQualifier?: AdditionalQualifier
  ) {
    this.filter$(fields, type, page, pageSize, mainId, qualifier, sorting, additionalQualifier)
      .pipe(take(1))
      .subscribe()
  }

  private idAndLastUpdateEqual(e1: Entity, e2: Entity): boolean {
    if (!e1 && !e2) {
      return true
    }
    if (!e1 || !e2) {
      return false
    }

    return (
      e1[ENTITY_FIELD_KEY_ID] === e2[ENTITY_FIELD_KEY_ID] &&
      e1[ENTITY_FIELD_KEY_LAST_UPDATED] === e2[ENTITY_FIELD_KEY_LAST_UPDATED]
    )
  }
}
